// The command view used to rebuild #app with innerHTML once per second. That
// restarted every CSS transition, wiped every keyframe animation mid-flight,
// and threw away the canvas element (and with it the whole WorldScene) sixty
// times a minute. Nothing could ever feel alive.
//
// morph() patches the existing DOM in place instead: same-shaped nodes are
// reused, so animations keep running, :hover survives, and the canvas keeps its
// identity. Two escape hatches:
//   data-static — subtree is never touched (the canvas host)
//   data-live   — text content is owned by the rAF value loop, not by markup
const SKIP_ATTRS = new Set(['open', 'data-live-value']);

function sameNode(left, right) {
  if (left.nodeType !== right.nodeType) return false;
  if (left.nodeType !== Node.ELEMENT_NODE) return true;
  if (left.tagName !== right.tagName) return false;
  return (left.dataset?.key || null) === (right.dataset?.key || null);
}

function patchAttributes(target, source) {
  for (const attr of Array.from(source.attributes)) {
    if (SKIP_ATTRS.has(attr.name)) continue;
    if (target.getAttribute(attr.name) !== attr.value) target.setAttribute(attr.name, attr.value);
  }
  for (const attr of Array.from(target.attributes)) {
    if (SKIP_ATTRS.has(attr.name)) continue;
    if (!source.hasAttribute(attr.name)) target.removeAttribute(attr.name);
  }
  // Buttons keep a live `disabled` property that attribute patching alone can
  // leave stale after the element has been focused/pressed.
  if ('disabled' in target) target.disabled = source.hasAttribute('disabled');
}

function patchChildren(target, source) {
  const targetNodes = Array.from(target.childNodes);
  const sourceNodes = Array.from(source.childNodes);
  const count = Math.max(targetNodes.length, sourceNodes.length);
  for (let index = 0; index < count; index += 1) {
    const existing = targetNodes[index];
    const next = sourceNodes[index];
    if (!next) { existing.remove(); continue; }
    if (!existing) { target.appendChild(next.cloneNode(true)); continue; }
    if (!sameNode(existing, next)) { existing.replaceWith(next.cloneNode(true)); continue; }
    morph(existing, next);
  }
}

export function morph(target, source) {
  if (target.nodeType === Node.TEXT_NODE) {
    if (target.nodeValue !== source.nodeValue) target.nodeValue = source.nodeValue;
    return target;
  }
  if (target.nodeType !== Node.ELEMENT_NODE) return target;
  if (target.hasAttribute('data-static')) {
    patchAttributes(target, source);
    return target;
  }
  patchAttributes(target, source);
  if (target.hasAttribute('data-live')) return target;
  patchChildren(target, source);
  return target;
}

// Replace the contents of `root` with `html`, reusing whatever already matches.
export function morphInto(root, html) {
  const staging = document.createElement('div');
  staging.innerHTML = html;
  patchChildren(root, staging);
  return root;
}
